import {
  Book,
  Chat,
  ChatBot,
  Document,
  Flash,
  Forum,
  Home,
  Search,
  SearchAdvanced,
  Send,
} from "@carbon/react/icons";
import { Button, Tag } from "@carbon/react";
import type { ComponentType, ReactNode } from "react";
import { DocumentTabs } from "@/app/(app)/documents/[id]/document-tabs";
import { Card } from "@/components/Card";
import { PageHeader } from "@/components/PageHeader";
import { PageToolbar } from "@/components/PageToolbar";
import { ResultRow } from "@/components/ResultRow";
import { WikiClaimCard } from "@/components/WikiClaimCard";
import { ClaimEditedLink } from "@/components/ClaimEditedLink";
import { agentsCopy } from "@/lib/ui/agents";
import { chatHistoryCopy } from "@/lib/ui/chat-history";
import { documentListCopy } from "@/lib/ui/document-list";
import { chatSourcesCopy } from "@/lib/ui/chat-sources";
import { chatDeepModeLabel, chatFastModeLabel } from "@/lib/ui/chat-modes";
import { chatTrustLabel } from "@/lib/ui/chat-trust";
import { compareSideTitle } from "@/lib/ai/compare-format";
import { parseStatusLabel } from "@/lib/ui/document-status";
import {
  documentVersionCopy,
} from "@/lib/ui/document-view";
import { formatFileSize } from "@/lib/ui/format";
import { appNavItems, type AppNavId } from "@/lib/ui/nav";
import {
  askWikiLabel,
  relationTypeLabel,
  wikiListCopy,
  wikiTrail,
} from "@/lib/ui/wiki";

const NAV_ICONS: Record<
  AppNavId,
  ComponentType<{ size?: number }>
> = {
  home: Home,
  search: Search,
  chat: Chat,
  documents: Document,
  wiki: Book,
  agents: ChatBot,
  transcripts: Forum,
};

function AppFrame({
  active,
  chat = false,
  children,
}: {
  active: AppNavId;
  chat?: boolean;
  children: ReactNode;
}) {
  const items = appNavItems();

  return (
    <div
      className={`landing-app${chat ? " landing-app--chat" : ""}`}
      aria-hidden="true"
    >
      <div className="landing-app-header">
        <span className="landing-app-prefix">PinWiki</span>
        ООО «Северлайн»
      </div>
      <div className="landing-app-body">
        <aside className="landing-app-sidenav">
          {items.map((item) => {
            const Icon = NAV_ICONS[item.id];
            return (
              <span
                key={item.id}
                className={`landing-app-nav${item.id === active ? " is-current" : ""}`}
              >
                <Icon size={16} />
                {item.label}
              </span>
            );
          })}
        </aside>
        <div className="landing-app-main">{children}</div>
      </div>
    </div>
  );
}

export function ChatProductMock() {
  return (
    <AppFrame active="chat" chat>
      <PageHeader
        title="Чат"
        description="Ответы только по доступным вам документам."
        crumbs={[{ label: chatHistoryCopy.nav }]}
      />
      <div className="landing-app-chat">
        <div className="landing-app-chat-main">
          <div className="chat-thread-pane">
            <div className="chat-thread">
              <div className="chat-msg-user-wrap">
                <div className="chat-msg chat-msg-user">
                  Сколько дней отпуска по политике 2025?
                </div>
              </div>
              <div className="chat-msg chat-msg-assistant">
                <div className="chat-msg-meta">
                  <span>
                    {chatTrustLabel({ status: "answered", sourceCount: 2 })}
                  </span>
                </div>
                <div className="chat-answer">
                  <p className="chat-answer-p">
                    По политике отпусков 2025 сотруднику положено 28 календарных
                    дней в год
                    <span className="chat-cite chat-cite--document is-on">1</span>
                    . Перенос на следующий год — не больше 5 дней
                    <span className="chat-cite chat-cite--document">2</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="chat-composer-form">
            <div className="chat-composer">
              <textarea
                className="chat-composer-input"
                rows={2}
                readOnly
                tabIndex={-1}
                placeholder="Задайте вопрос…"
              />
              <div className="chat-composer-bar">
                <div className="chat-composer-modes">
                  <span className="chat-composer-icon">
                    <Flash size={18} />
                    <span className="chat-composer-mode-label">
                      {chatFastModeLabel}
                    </span>
                  </span>
                  <span className="chat-composer-icon">
                    <SearchAdvanced size={18} />
                    <span className="chat-composer-mode-label">
                      {chatDeepModeLabel}
                    </span>
                  </span>
                </div>
                <span className="chat-composer-icon chat-composer-send">
                  <Send size={18} />
                </span>
              </div>
            </div>
          </div>
        </div>
        <aside className="landing-app-sources">
          <div className="chat-sources">
            <div className="chat-sources-head">
              {chatSourcesCopy.title}
              <span className="chat-sources-head-count">2</span>
            </div>
            <div className="chat-sources-body">
              <div className="chat-sources-list">
                <div className="chat-sources-group">
                  <h3 className="chat-sources-group-title">
                    {chatSourcesCopy.document}
                    <span className="chat-sources-head-count">2</span>
                  </h3>
                  <ul>
                    <li>
                      <div className="chat-source-row is-on">
                        <span className="chat-source-cites">
                          <span className="chat-cite chat-cite--document is-on">
                            1
                          </span>
                        </span>
                        <span className="chat-source-row-main">
                          <span className="chat-source-row-title">
                            Политика отпусков 2025
                          </span>
                          <span className="chat-source-row-meta">
                            стр. 4 · версия 1
                          </span>
                        </span>
                      </div>
                    </li>
                    <li>
                      <div className="chat-source-row">
                        <span className="chat-source-cites">
                          <span className="chat-cite chat-cite--document">2</span>
                        </span>
                        <span className="chat-source-row-main">
                          <span className="chat-source-row-title">
                            Политика отпусков 2025
                          </span>
                          <span className="chat-source-row-meta">
                            стр. 6 · версия 1
                          </span>
                        </span>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </AppFrame>
  );
}

export function WikiProductMock() {
  return (
    <AppFrame active="wiki">
      <div className="page-stack">
        <PageHeader
          title="Испытательный срок"
          description="Факты из доступных вам документов."
          crumbs={wikiTrail({ label: "Испытательный срок" })}
        />
        <PageToolbar>
          <Button type="button" kind="tertiary" size="md">
            {askWikiLabel}
          </Button>
        </PageToolbar>
        <section className="wiki-facts-section">
          <h2 className="wiki-facts-heading">{wikiListCopy.facts}</h2>
          <ul className="wiki-review-list">
            <li>
              <WikiClaimCard
                claimId="mock-verified"
                text="3 месяца"
                edited
                status="VERIFIED"
                confidence={0.9}
                sourceHref="#waitlist"
                sourceTitle="Трудовой договор"
              />
            </li>
            <li>
              <WikiClaimCard
                claimId="mock-conflicted"
                text="2 месяца"
                status="VERIFIED"
                confidence={0.7}
                sourceHref="#waitlist"
                sourceTitle="Оффер кандидату"
                conflictCount={1}
                selected
              />
            </li>
          </ul>
        </section>
      </div>
    </AppFrame>
  );
}

export function WikiRelationsProductMock() {
  return (
    <AppFrame active="wiki">
      <div className="page-stack">
        <PageHeader
          title="Политика отпусков 2025"
          description="Связи из доступных вам документов."
          crumbs={wikiTrail({ label: "Политика отпусков 2025" })}
        />
        <section className="wiki-facts-section">
          <h2 className="wiki-facts-heading">{wikiListCopy.relations}</h2>
          <ul className="result-list">
            <li>
              <ResultRow
                href="#waitlist"
                title={`Сторона — ООО «Северлайн»`}
                meta={relationTypeLabel("PARTY")}
              />
            </li>
            <li>
              <ResultRow
                href="#waitlist"
                title="Относится к Трудовой договор"
                meta={relationTypeLabel("BELONGS_TO")}
              />
            </li>
            <li>
              <ResultRow
                href="#waitlist"
                title="Заменяет Политика отпусков 2023"
                meta={relationTypeLabel("SUPERSEDES")}
              />
            </li>
          </ul>
        </section>
      </div>
    </AppFrame>
  );
}

export function VersionsProductMock() {
  return (
    <AppFrame active="documents">
      <div className="page-stack">
        <PageHeader
          title="Политика отпусков"
          description="PDF · две версии, текущая — 2025"
          crumbs={[
            { label: documentListCopy.title, href: "/documents" },
            { label: "Политика отпусков" },
          ]}
        />
        <DocumentTabs
          defaultSelectedIndex={3}
          overview={<p>Карточка документа</p>}
          content={<p>Текст</p>}
          analysis={<p>AI-анализ</p>}
          versions={
            <div className="document-versions">
              <section>
                <h3 className="document-versions-heading">
                  {documentVersionCopy.listTitle}
                </h3>
                <ul className="result-list">
                  <li>
                    <ResultRow
                      title={`Версия 2 · ${documentVersionCopy.current}`}
                      meta={`Политика отпусков 2025.pdf · ${formatFileSize(184320)} · ${parseStatusLabel("DONE")}`}
                      trailing={
                        <Tag type="green" size="sm">
                          {documentVersionCopy.current}
                        </Tag>
                      }
                    />
                  </li>
                  <li>
                    <ResultRow
                      title="Версия 1"
                      meta={`Политика отпусков 2023.pdf · ${formatFileSize(172044)} · ${parseStatusLabel("DONE")}`}
                    />
                  </li>
                </ul>
              </section>
            </div>
          }
        />
      </div>
    </AppFrame>
  );
}

export function CompareProductMock() {
  return (
    <AppFrame active="agents">
      <div className="page-stack">
        <PageHeader
          title={agentsCopy.compareName}
          description={agentsCopy.compareDescription}
          crumbs={[
            { label: agentsCopy.nav, href: "/agents" },
            { label: agentsCopy.compareName },
          ]}
        />
        <ul className="result-list">
          <li>
            <ResultRow
              href="#waitlist"
              title={compareSideTitle("Трудовой договор", 2)}
              meta={agentsCopy.reviseDraftLabel}
            />
          </li>
          <li>
            <ResultRow
              href="#waitlist"
              title={compareSideTitle("Трудовой договор", 1)}
              meta={agentsCopy.reviseNormLabel}
            />
          </li>
        </ul>
        <Card variant="review" className="agent-briefing-report">
          <div className="agent-report">
            <section className="agent-report-section">
              <h3 className="agent-report-heading">Где расходятся</h3>
              <p>
                Испытательный срок: 2 месяца в черновике, 3 месяца в норме.
              </p>
            </section>
            <section className="agent-report-section">
              <h3 className="agent-report-heading">Что общего</h3>
              <p>Сторона — ООО «Северлайн». Отпуск — 28 календарных дней.</p>
            </section>
          </div>
        </Card>
      </div>
    </AppFrame>
  );
}

export function ReviseProductMock() {
  return (
    <AppFrame active="agents">
      <div className="page-stack">
        <PageHeader
          title={agentsCopy.reviseName}
          description={agentsCopy.reviseDescription}
          crumbs={[
            { label: agentsCopy.nav, href: "/agents" },
            { label: agentsCopy.reviseName },
          ]}
        />
        <article className="revise-doc">
          <section className="revise-doc-section">
            <h2 className="revise-doc-heading">Испытательный срок</h2>
            <div className="revise-doc-p is-on">
              <p>
                <span className="revise-mark">
                  <del>2 месяца</del>
                </span>{" "}
                <span className="revise-mark">
                  <ins>3 месяца</ins>
                </span>
                <ClaimEditedLink claimId="mock-verified" compact />
              </p>
              <div className="revise-doc-hitl">
                <Button type="button" size="md" kind="primary">
                  {agentsCopy.reviseAccept}
                </Button>
                <Button type="button" size="md" kind="danger--tertiary">
                  {agentsCopy.reviseReject}
                </Button>
              </div>
            </div>
          </section>
          <p className="settings-form-help">{agentsCopy.reviseSave}</p>
        </article>
      </div>
    </AppFrame>
  );
}
